import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';

// Components
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import AdvancedTable from '../../components/AdvancedTable';
import AssignDoctorModal from '../../components/Modal/AssignDoctorModal';
import { plusicon, refreshicon } from '../../components/imagepath';

// Actions
import {
  GetAllTreatmentSlot,
  ActionUpdateTreatmentSlot,
} from '../../store/Actions/TreatmentSlotAction';

// Utils
import { matchPermissions } from '../../utils/Permission';
import { PERMISSIONS } from '../../constants';

// Custom Hooks
import { useTreatmentSlotColumns } from './hooks/useTreatmentSlotColumns';
import { useTreatmentSlotFilters } from './hooks/useTreatmentSlotFilters';
import dayjs from 'dayjs';

const TreatmentSlot = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector(state => state?.auth);
  const { loader } = useSelector(state => state?.TreatmentSlotBooking);
  const matchPermissionsForUser = matchPermissions(userDetails);

  // Modal state
  const [modal, setModal] = useState({ is: false, id: '' });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Permissions
  const canView = matchPermissionsForUser(PERMISSIONS.TREATMENTS.SLOTS);
  const canCreate = matchPermissionsForUser(PERMISSIONS.TREATMENTS.CREATE);
  const canUpdate = matchPermissionsForUser(PERMISSIONS.TREATMENTS.UPDATE);
  const canExport = matchPermissionsForUser(PERMISSIONS.TREATMENTS.EXPORT);

  // Status update handler
  const handleStatusUpdate = useCallback(
    (recordId, newStatus) => {
      dispatch(
        ActionUpdateTreatmentSlot(
          {
            Query: recordId,
            obj: { status: newStatus },
          },
          () => setRefreshTrigger(prev => prev + 1)
        )
      );
    },
    [dispatch]
  );

  // Assign doctor handler
  const handleAssignDoctor = useCallback((recordId) => {
    setModal({ is: true, id: recordId });
  }, []);

  // Close modal handler
  const handleCloseModal = useCallback(() => {
    setModal({ is: false, id: '' });
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Fetch data function for AdvancedTable
  const fetchData = useCallback(
    async (params) => {
      const {
        page = 1,
        perPage = 10,
        sortField,
        sortOrder,
        search,
        status = 'Upcoming',
        customerName,
        treatmentName,
        phoneNo,
        dateRange_start,
        dateRange_end,
      } = params;

      // Build query string
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', perPage);

      if (status) queryParams.append('status', status);
      if (sortField) queryParams.append('sortOn', sortField);
      if (sortOrder) queryParams.append('sort', sortOrder);
      if (search) queryParams.append('search', search);
      if (customerName) queryParams.append('customerName', customerName);
      if (treatmentName) queryParams.append('treatmentName', treatmentName);
      if (phoneNo) queryParams.append('phoneNo', phoneNo);

      // Handle date range from filters
      if (dateRange_start && dayjs(dateRange_start).isValid()) queryParams.append('startDate', dayjs(dateRange_start).format('YYYY-MM-DD'));
      if (dateRange_end && dayjs(dateRange_end).isValid()) queryParams.append('endDate', dayjs(dateRange_end).format('YYYY-MM-DD'));

      const queryString = `?${queryParams.toString()}`;

      // Return a Promise that resolves when the API call completes
      return new Promise((resolve) => {
        dispatch(
          GetAllTreatmentSlot({ Query: queryString }, (responseData) => {
            // responseData is the actual API response data
            if (responseData) {
              resolve({
                data: responseData?.docs || [],
                total: responseData?.totalDocs || 0,
              });
            } else {
              // Handle error case
              resolve({
                data: [],
                total: 0,
              });
            }
          })
        );
      });
    },
    [dispatch, refreshTrigger]
  );

  // Get columns configuration
  const columns = useTreatmentSlotColumns({
    canUpdate,
    onStatusUpdate: handleStatusUpdate,
    onAssignDoctor: handleAssignDoctor,
  });

  // Get filter configuration
  const filterConfig = useTreatmentSlotFilters();

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/treatment-slot?page=1">Treatment Booking</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">
                    Treatment Booking List
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Advanced Table */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card card-table show-entire">
                <div className="card-body">
                  {/* Page Header with Add Button */}
                  <div className="page-table-header mb-2">
                    <div className="row align-items-center justify-content-between">
                      <div className="col-12 col-md-8">
                        <div className="doctor-table-blk">
                          <h3>Treatment Booking List</h3>
                          <div className="doctor-search-blk">
                            <div className="add-group">
                              {canCreate && (
                                <Link
                                  to="/treatment-slot/add-treatment-slot"
                                  className="btn btn-primary add-pluss ms-2"
                                >
                                  <img src={plusicon} alt="Add" />
                                </Link>
                              )}

                              <Link
                                to="/treatment-slot?page=1"
                                className="btn btn-primary doctor-refresh ms-2"
                              >
                                <img src={refreshicon} alt="#" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {canView ? (
                    <AdvancedTable
                      resource="treatment-slot"
                      columns={columns}
                      fetchData={fetchData}
                      loading={loader}
                      enableSearch={false}
                      enableFilters={true}
                      enableRefresh={true}
                      enableExport={canExport}
                      enableColumnManager={true}
                      enableHorizontalScrollFeature={true}
                      enableToolBar={true}
                      filterConfig={filterConfig}
                      defaultPageSize={10}
                      defaultFilters={{ status: 'Upcoming' }}
                      exportFileName="treatment-bookings"
                      persistState={true}
                      tableId="treatment-bookings-v2"
                      stickyHeader={true}
                      striped={true}
                      highlightOnHover={true}
                      density="comfortable"
                      key={`treatment-table-${refreshTrigger}`}
                    />
                  ) : (
                    <div className="permission-denied-message">
                      You do not have permission to view treatment bookings.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assign Doctor Modal */}
      <AssignDoctorModal
        IsOpen={modal}
        slotType="treatment"
        initialValues={{
          doctorId: '',
          doctorName: '',
        }}
        Isclose={handleCloseModal}
      />
    </>
  );
};

export default TreatmentSlot;
